import { ChevronDown, Plus, Minus, LucideX } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { ProxyTypeMetas } from '@fd/common/biz/proxyType';
import { useCartStore } from '@/store/cartStore';
import { ProxyInfoType } from '@/model/proxyTypes';
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup';
import Flex from '../ui/Flex';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/Accordion';
import { useToast } from '@/hooks/useToast';
import { calculateProxyTypeSubtotals } from '@fd/common/biz/pricing';
import { trafficTiers } from '@fd/common/biz/traffic';
import { useSearchParams } from 'react-router';
import { TrafficZod } from '@fd/common/biz/trafficZod';
import Bullet from '../ui/Bullet';
import DiscountBadge from './DiscountBadge';
import { DesktopView, MobileView } from '../responsive';
import { Button } from '@fd/frontend/components/ui/Button';
import useMobile from '@fd/frontend/hooks/useMobile';

type ProxyCardProps = {
  proxyInfo: ProxyInfoType;
  isExpanded: boolean;
  onToggle: () => void;
};

export function ProxyCard({ proxyInfo, isExpanded, onToggle }: ProxyCardProps) {
  const searchParams = useSearchParams()[0];

  const [quantity, setQuantityRaw] = useState(proxyInfo.defaultQuantity);
  const [inputValue, setInputValue] = useState(quantity.toString());
  const { addItem, items, removeItem } = useCartStore();
  const { toast } = useToast();
  const isMobile = useMobile();
  const minQuantity = isMobile ? 2 : proxyInfo.minQuantity;
  const currentItemIndex = items.findIndex(
    (item) => item.type === proxyInfo.type
  );
  const currentItem = items[currentItemIndex];
  const cartTotals = calculateProxyTypeSubtotals({
    trafficGb: currentItem?.quantity ?? 0,
    proxyType: ProxyTypeMetas[proxyInfo.type],
  });
  const currentTotals = calculateProxyTypeSubtotals({
    trafficGb: quantity,
    proxyType: ProxyTypeMetas[proxyInfo.type],
  });

  useEffect(() => {
    const proxyType = searchParams.get('type');
    if (proxyType === proxyInfo.type) {
      const parsedTrafficGb = TrafficZod.safeParse(
        searchParams.get('trafficGb')
      );
      setQuantity(Math.max(minQuantity, parsedTrafficGb.data ?? 5));
    }
  }, [proxyInfo, searchParams, minQuantity]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow empty input for typing purposes
    if (value === '') {
      setInputValue('');
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(value)) {
      return;
    }

    setInputValue(value);
  };

  const handleInputBlur = () => {
    // Convert to number and ensure minimum value of 5
    const numValue =
      inputValue === ''
        ? minQuantity
        : Math.max(minQuantity, Number.parseInt(inputValue, 10));
    setInputValue(numValue.toString());
    setQuantityRaw(numValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  function setQuantity(numValue: number) {
    setQuantityRaw(numValue);
    setInputValue(numValue.toString());
  }

  return (
    <Accordion type="single" collapsible className="w-full" value="open">
      <AccordionItem value={isExpanded ? 'open' : 'closed'} className="w-full">
        <div
          className={clsx(
            'bg-white rounded-xl border border-[#C8C8C8] w-full md:w-[780px] transition-all',
            'hover:border hover:border-primary-purple',
            isExpanded &&
              'focus-within:border-primary-purple focus-within:ring-2 focus-within:ring-primary-purple/30',
            currentItem && 'border-primary-purple ring-2 ring-primary-purple/30'
          )}
        >
          <AccordionTrigger className="w-full select-text" onClick={onToggle}>
            <div
              className={clsx(
                'px-5 cursor-pointer flex',
                isExpanded ? 'pt-8 pb-4' : 'py-8'
              )}
            >
              <div className="flex items-center justify-between flex-1 gap-4">
                <div className="flex gap-3 w-full flex-wrap sm:flex-nowrap">
                  <DesktopView>
                    <div className="bg-proxy-icon-bg h-11 aspect-square rounded-lg items-center justify-center flex">
                      {proxyInfo.icon}
                    </div>
                  </DesktopView>
                  <MobileView>
                    <Flex.Row className="justify-between w-full items-center">
                      <div className="bg-proxy-icon-bg h-11 aspect-square rounded-lg items-center justify-center flex">
                        {proxyInfo.icon}
                      </div>
                      <ChevronDown
                        className={clsx(
                          'h-5 w-5 text-secondary transition-transform duration-300',
                          isExpanded && 'rotate-180'
                        )}
                      />
                    </Flex.Row>
                  </MobileView>
                  <div className="w-full flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <div className="font-medium text-proxy-text text-xl">
                        {proxyInfo.title}
                      </div>
                      <p className="text-sm text-secondary">
                        {proxyInfo.description}
                      </p>
                    </div>
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <div className="mr-40 sm:mr-0 hidden sm:block">
                          Best for:
                        </div>
                        {proxyInfo.bullets.map((bullet) => (
                          <Bullet key={bullet}>{bullet}</Bullet>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <DesktopView>
                  <div className="flex gap-5">
                    {!isExpanded && currentItem && (
                      <Flex.Col className="hidden sm:flex gap-1 items-center">
                        <span className="font-semibold">
                          {cartTotals.totalPriceText}
                        </span>
                        <span className="font-normal">month</span>
                      </Flex.Col>
                    )}
                    <ChevronDown
                      className={clsx(
                        'h-5 w-5 text-secondary transition-transform duration-300',
                        isExpanded && 'rotate-180'
                      )}
                    />
                  </div>
                </DesktopView>
              </div>
            </div>
            {!isExpanded && currentItem && (
              <MobileView>
                <div className="px-5 py-4 bg-neutral-100 rounded-b-lg border-t border-gray-200 border-2">
                  <Flex.Row className="flex items-center justify-between w-full">
                    <Flex.Row className="gap-2">
                      <div className="font-medium">
                        {cartTotals.totalPriceText}
                        <span className="text-secondary font-normal">
                          {' '}
                          / month
                        </span>
                      </div>
                    </Flex.Row>
                    <Flex.Row
                      className="gap-2 font-medium text-primary-purple stroke-primary-purple"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(currentItemIndex);
                        toast({
                          title: 'Proxy removed from order',
                          description: `${quantity} ${proxyInfo.type} GB removed from order`,
                        });
                      }}
                    >
                      <LucideX />
                      Remove
                    </Flex.Row>
                  </Flex.Row>
                </div>
              </MobileView>
            )}
          </AccordionTrigger>
          <AccordionContent>
            <div>
              <div className="px-5 sm:px-20 pb-5 flex flex-col gap-5">
                <div className="border-proxy-border border-t-2"></div>
                <div className="space-y-3">
                  <RadioGroup value="shared-gb">
                    {proxyInfo.paymentModels.map((model) => (
                      <label
                        key={model.id}
                        className="flex items-start gap-2 cursor-pointer group hover:bg-proxy-hover p-1 rounded-md transition-colors"
                      >
                        <Flex.Row className="gap-3">
                          <RadioGroupItem value={model.id} className="mt-1" />
                          <div>
                            <div className="text-primary font-medium">
                              {model.title}{' '}
                              <span className="text-sm text-secondary">
                                {model.price}
                              </span>
                            </div>
                            <div className="text-sm text-secondary">
                              {model.description}
                            </div>
                          </div>
                        </Flex.Row>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Quantity selector */}
                <div className="mb-4">
                  <div className="flex border border-proxy-border-light rounded-md max-w-96">
                    <button
                      className="px-3 py-2"
                      onClick={() => {
                        console.log('minus');
                        setQuantity(
                          Math.max(minQuantity, quantity - 1)
                        );
                      }}
                      disabled={quantity === minQuantity}
                    >
                      <Minus
                        className={clsx(
                          'h-5 w-5 stroke-secondary',
                          quantity === minQuantity && 'stroke-secondary/20'
                        )}
                      />
                    </button>
                    <div className="flex-1 relative">
                      <input
                        id={`traffic-quantity-${proxyInfo.type}`}
                        type="text"
                        inputMode="numeric"
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        className="w-full h-full text-center py-2 focus:outline-none focus:ring-1 focus:ring-proxy-primary"
                        aria-label="Traffic quantity in GB"
                      />
                      <span className="hidden sm:block absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary">
                        GB
                      </span>
                    </div>
                    <button
                      className="px-3 py-2 "
                      onClick={() => {
                        setQuantity(quantity + 1);
                      }}
                    >
                      <Plus className="h-5 w-5 text-secondary" />
                    </button>
                  </div>
                  <Flex.Row className="gap-2 mt-3">
                    {trafficTiers.map((tier) => (
                      <DiscountBadge
                        key={tier.amountGb}
                        discount={tier.discountPct}
                        quantity={tier.amountGb}
                        onClick={setQuantity}
                      />
                    ))}
                  </Flex.Row>
                </div>
              </div>

              {/* Price and add button */}
              <div className="px-5 py-4 bg-neutral-100 rounded-b-xl border-t border-gray-200 border-2">
                <Flex.Row className="flex items-center justify-between w-full">
                  <span className="hidden sm:block text-sm text-secondary">
                    Unused traffic will roll over to next billing period
                  </span>
                  <Flex.Row className="gap-5 items-center w-full sm:w-auto">
                    <Flex.Row className="gap-2">
                      {currentTotals.tierDiscountPct > 0 && (
                        <div className="text-lg font-medium text-muted line-through">
                          {currentTotals.subtotalPriceText}
                        </div>
                      )}
                      <div className="font-medium">
                        {currentTotals.totalPriceText}
                        <span className="text-secondary font-normal">
                          {' '}
                          / month
                        </span>
                      </div>
                    </Flex.Row>
                    <Button
                      className="text-sm font-medium flex-1 sm:flex-none"
                      onClick={() => {
                        addItem({ type: proxyInfo.type, quantity });
                        toast({
                          title: 'Proxy added to order',
                          description: `${quantity} ${proxyInfo.type} GB added to order`,
                        });
                        onToggle();
                      }}
                    >
                      Add to cart
                    </Button>
                  </Flex.Row>
                </Flex.Row>
              </div>
            </div>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
}
